import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Shield, ChevronLeft, Search, Loader2, AlertTriangle,
  Eye, Users, UserCheck, Clock, CheckCircle2, XCircle,
  MapPin, ArrowRight, ScanFace, FileWarning, Bell, ShieldAlert, Footprints,
} from "lucide-react";
import { useLocation } from "wouter";
import CommandNav from "@/components/CommandNav";

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  detected: { label: "Detectado", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: Eye },
  identified: { label: "Identificado", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: UserCheck },
  classified: { label: "Classificado", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: ScanFace },
  agent_dispatched: { label: "Agente Acionado", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: Users },
  in_progress: { label: "Em Atendimento", color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30", icon: Clock },
  resolved: { label: "Resolvido", color: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle2 },
  closed: { label: "Encerrado", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", icon: XCircle },
};

const CLASSIFICATION_CONFIG: Record<string, { label: string; color: string }> = {
  suspect_identified: { label: "Suspeito Identificado", color: "text-red-400" },
  victim_identified: { label: "Vítima Identificada", color: "text-amber-400" },
  monitored_person: { label: "Pessoa Monitorada", color: "text-blue-400" },
  unidentified_person: { label: "Não Identificada", color: "text-zinc-400" },
  under_analysis: { label: "Em Análise", color: "text-yellow-400" },
  confirmed_incident: { label: "Incidente Confirmado", color: "text-red-500" },
};

const EVENT_TYPE_CONFIG: Record<string, { label: string; icon: any }> = {
  facial_recognition: { label: "Reconhecimento Facial", icon: ScanFace },
  manual_report: { label: "Relato Manual", icon: FileWarning },
  alert: { label: "Alerta", icon: Bell },
  incident: { label: "Incidente", icon: ShieldAlert },
  patrol_check: { label: "Ronda", icon: Footprints },
};

export default function Events() {
  const [, navigate] = useLocation();
  // V9.1: Read URL search params for pre-filtering from Dashboard metrics links
  const initialStatus = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const s = params.get('status');
      if (s === 'active') return 'in_progress'; // "Abertos" maps to active statuses
      if (s === 'conflicts') return 'all'; // conflicts is a classification, not status
      if (s && STATUS_CONFIG[s]) return s;
    } catch {}
    return 'all';
  })();
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: events, isLoading } = trpc.opEvent.list.useQuery();
  const { data: counts } = trpc.opEvent.counts.useQuery();

  const filtered = events?.filter(e => {
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        (e.personName && e.personName.toLowerCase().includes(q)) ||
        (e.person2Name && e.person2Name.toLowerCase().includes(q)) ||
        (e.sector && e.sector.toLowerCase().includes(q)) ||
        (e.eventSubtype && e.eventSubtype.toLowerCase().includes(q)) ||
        String(e.id).includes(q)
      );
    }
    return true;
  }) || [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header — ELLA Command Platform */}
      <div className="sticky top-0 z-50">
        <CommandNav pageTitle="Eventos Operacionais" />
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <CountCard label="Total" value={counts?.total || 0} color="text-foreground" active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
          <CountCard label="Detectados" value={counts?.detected || 0} color="text-yellow-400" active={statusFilter === "detected"} onClick={() => setStatusFilter(statusFilter === "detected" ? "all" : "detected")} />
          <CountCard label="Identificados" value={counts?.identified || 0} color="text-blue-400" active={statusFilter === "identified"} onClick={() => setStatusFilter(statusFilter === "identified" ? "all" : "identified")} />
          <CountCard label="Classificados" value={counts?.classified || 0} color="text-purple-400" active={statusFilter === "classified"} onClick={() => setStatusFilter(statusFilter === "classified" ? "all" : "classified")} />
          <CountCard label="Ag. Acionado" value={counts?.agent_dispatched || 0} color="text-orange-400" active={statusFilter === "agent_dispatched"} onClick={() => setStatusFilter(statusFilter === "agent_dispatched" ? "all" : "agent_dispatched")} />
          <CountCard label="Em Atendimento" value={counts?.in_progress || 0} color="text-cyan-400" active={statusFilter === "in_progress"} onClick={() => setStatusFilter(statusFilter === "in_progress" ? "all" : "in_progress")} />
          <CountCard label="Resolvidos" value={counts?.resolved || 0} color="text-green-400" active={statusFilter === "resolved"} onClick={() => setStatusFilter(statusFilter === "resolved" ? "all" : "resolved")} />
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, setor ou ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-card border-border/50"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-card border-border/50">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Events List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-ella-purple-light" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <AlertTriangle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum evento encontrado</h3>
            <p className="text-sm text-muted-foreground">
              Eventos são gerados automaticamente pelo reconhecimento facial ou manualmente pelo operador.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((event) => {
              const statusCfg = STATUS_CONFIG[event.status || "detected"];
              const classCfg = CLASSIFICATION_CONFIG[event.classification || "under_analysis"];
              const typeCfg = EVENT_TYPE_CONFIG[event.eventType || "facial_recognition"];
              const StatusIcon = statusCfg?.icon || Eye;
              const TypeIcon = typeCfg?.icon || ScanFace;

              return (
                <div
                  key={event.id}
                  className="bg-card border border-border/50 rounded-lg p-4 hover:border-ella-purple/30 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/events/${event.id}`)}
                >
                  <div className="flex items-center gap-4">
                    {/* Thumbnail */}
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-secondary shrink-0">
                      {event.capturedImageUrl ? (
                        <img src={event.capturedImageUrl} alt="Captura" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <TypeIcon className="w-6 h-6 text-muted-foreground/30" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-muted-foreground">#{event.id}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${statusCfg?.color}`}>
                          <StatusIcon className="w-2.5 h-2.5 mr-1" />
                          {statusCfg?.label}
                        </Badge>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border-border/50 ${classCfg?.color}`}>
                          {classCfg?.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium truncate">
                          {event.personName || "Pessoa não identificada"}
                        </span>
                        {event.person2Name && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0 border-red-500/30 text-red-400 animate-pulse">
                            CONFLITO
                          </Badge>
                        )}
                        {event.confidenceScore && (
                          <span className="text-xs text-ella-purple-light font-mono">
                            {parseFloat(event.confidenceScore).toFixed(0)}% match
                          </span>
                        )}
                      </div>
                      {event.person2Name && (
                        <div className="text-[10px] text-red-400 mt-0.5">
                          vs {event.person2Name} ({event.person2Category === "restricted" ? "Agressor" : "Vítima"})
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <TypeIcon className="w-3 h-3" /> {typeCfg?.label}
                        </span>
                        {event.sector && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {event.sector}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {event.createdAt ? new Date(event.createdAt).toLocaleString("pt-BR") : ""}
                        </span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ArrowRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-ella-purple-light transition-colors shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CountCard({ label, value, color, active, onClick }: { label: string; value: number; color: string; active?: boolean; onClick?: () => void }) {
  return (
    <div
      className={`bg-card border rounded-lg p-3 text-center cursor-pointer transition-all hover:border-ella-purple/50 ${
        active ? 'border-ella-purple/60 ring-1 ring-ella-purple/30' : 'border-border/50'
      }`}
      onClick={onClick}
    >
      <div className={`text-xl font-bold ${color}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        {value}
      </div>
      <div className="text-[9px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
